import React, { useCallback, useEffect, useState } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  Select,
  Switch,
  message,
  Popconfirm,
  Spin,
} from 'antd';
import {
  GlobalOutlined,
  PlusOutlined,
  ReloadOutlined,
  EditOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import MainLayout from '../../../layouts/MainLayout';
import { superadminLanguageSettingsApi, show422Error } from '../../../utils/api';
import { PANEL_PRIMARY_BTN_CLASS } from '../../../constants/ui';
import { TableSkeleton } from '../../../components/SkeletonLoaders';

const STATUS_OPTIONS = [
  { value: 'enabled', label: 'فعال' },
  { value: 'disabled', label: 'غیرفعال' },
];

const extractLanguages = (res) => (Array.isArray(res?.data) ? res.data : []);
const extractFlags = (res) => (Array.isArray(res?.data) ? res.data : res?.flags || []);

const SuperadminLanguageSettings = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const [formOpen, setFormOpen] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [flagOptions, setFlagOptions] = useState([]);
  const [statusUpdatingId, setStatusUpdatingId] = useState(null);

  const fetchIndex = useCallback(async () => {
    setLoading(true);
    try {
      const res = await superadminLanguageSettingsApi.list();
      setRows(extractLanguages(res));
    } catch (error) {
      message.error(error?.message || 'خطا در دریافت زبان‌ها');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchIndex();
  }, [fetchIndex]);

  const loadFlags = useCallback(async () => {
    try {
      const res = await superadminLanguageSettingsApi.getCreateMeta();
      setFlagOptions(extractFlags(res));
    } catch {
      setFlagOptions([]);
    }
  }, []);

  const buildPayload = (values) => ({
    language_name: String(values.language_name || '').trim(),
    language_code: String(values.language_code || '').trim(),
    flag: values.flag,
    status: values.status,
    is_rtl: values.is_rtl ? 1 : 0,
  });

  const openCreate = async () => {
    setEditingId(null);
    setFormOpen(true);
    setFormLoading(true);
    form.resetFields();
    try {
      await loadFlags();
      form.setFieldsValue({
        status: 'enabled',
        is_rtl: false,
      });
    } finally {
      setFormLoading(false);
    }
  };

  const openEdit = async (id) => {
    setEditingId(id);
    setFormOpen(true);
    setFormLoading(true);
    form.resetFields();
    try {
      const res = await superadminLanguageSettingsApi.getEditMeta(id);
      const lang = res?.language || {};
      setFlagOptions(extractFlags(res));
      if (!lang?.id) {
        message.warning('زبان یافت نشد');
        setFormOpen(false);
        return;
      }
      const flags = extractFlags(res);
      const flagMatch = flags.find(
        (f) => f.code === lang.flag_code || String(f.id) === String(lang.flag_code),
      );
      form.setFieldsValue({
        language_name: lang.language_name,
        language_code: lang.language_code,
        flag: flagMatch?.code ?? lang.flag_code ?? lang.flag,
        status: lang.status || 'disabled',
        is_rtl: Number(lang.is_rtl) === 1,
      });
    } catch (error) {
      message.error(error?.message || 'خطا در دریافت زبان');
      setFormOpen(false);
    } finally {
      setFormLoading(false);
    }
  };

  const onSubmit = async () => {
    try {
      const values = await form.validateFields();
      const payload = buildPayload(values);
      setFormSubmitting(true);
      if (editingId) {
        await superadminLanguageSettingsApi.update(editingId, payload);
        message.success('زبان بروزرسانی شد');
      } else {
        await superadminLanguageSettingsApi.create(payload);
        message.success('زبان جدید ثبت شد');
      }
      setFormOpen(false);
      fetchIndex();
    } catch (error) {
      if (error?.errorFields) return;
      if (error?.name === 'ValidationError' || error?.details) show422Error(error, form);
      else message.error(error?.message || 'خطا در ذخیره');
    } finally {
      setFormSubmitting(false);
    }
  };

  const onDelete = async (id) => {
    try {
      await superadminLanguageSettingsApi.delete(id);
      message.success('زبان حذف شد');
      fetchIndex();
    } catch (error) {
      message.error(error?.message || 'خطا در حذف');
    }
  };

  const onToggleStatus = async (row, checked) => {
    const next = checked ? 'enabled' : 'disabled';
    setStatusUpdatingId(row.id);
    try {
      await superadminLanguageSettingsApi.updateStatus(row.id, next);
      message.success('وضعیت بروزرسانی شد');
      setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, status: next } : r)));
    } catch (error) {
      message.error(error?.message || 'خطا در تغییر وضعیت');
    } finally {
      setStatusUpdatingId(null);
    }
  };

  const columns = [
    {
      title: 'زبان',
      key: 'name',
      ellipsis: true,
      render: (_, r) => (
        <div className="min-w-0">
          <div className="font-medium text-gray-800 dark:text-gray-100">{r.language_name}</div>
          <div className="text-xs text-gray-500" dir="ltr">
            {r.language_code}
          </div>
        </div>
      ),
    },
    {
      title: 'پرچم',
      dataIndex: 'flag_code',
      key: 'flag_code',
      width: 90,
      render: (c) => <Tag>{c || '—'}</Tag>,
    },
    {
      title: 'راست به چب',
      dataIndex: 'is_rtl',
      key: 'is_rtl',
      width: 72,
      render: (v) => (Number(v) === 1 ? <Tag color="blue">بله</Tag> : <Tag>خیر</Tag>),
    },
    {
      title: 'فعال',
      key: 'status',
      width: 100,
      render: (_, r) => (
        <Switch
          checked={r.status === 'enabled'}
          loading={statusUpdatingId === r.id}
          onChange={(checked) => onToggleStatus(r, checked)}
          checkedChildren="فعال"
          unCheckedChildren="غیرفعال"
        />
      ),
    },
    {
      title: 'عملیات',
      key: 'actions',
      width: 120,
      fixed: 'right',
      render: (_, r) => (
        <Space size="small">
          <Button type="text" icon={<EditOutlined />} aria-label="ویرایش" onClick={() => openEdit(r.id)} />
          <Popconfirm title="حذف این زبان؟" okText="حذف" cancelText="لغو" onConfirm={() => onDelete(r.id)}>
            <Button type="text" danger icon={<DeleteOutlined />} aria-label="حذف" />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <MainLayout>
      <div className="custom-font w-full min-w-0" style={{ fontFamily: 'CustomFont' }}>
        <Card className="shadow-xl border-0" styles={{ body: { padding: 0 } }}>
          <div className="flex flex-wrap items-center justify-between gap-4 border-b border-gray-200 bg-white p-4 sm:p-6 dark:border-gray-700 dark:bg-gray-800 rounded-t-xl">
            <div className="flex min-w-0 items-center gap-3">
              <div className="flex h-11 w-11 sm:h-12 sm:w-12 flex-shrink-0 items-center justify-center rounded-xl bg-sky-50 dark:bg-sky-900/25">
                <GlobalOutlined className="text-lg sm:text-xl text-sky-600 dark:text-sky-400" />
              </div>
              <div className="min-w-0">
                <h1 className="m-0 text-xl sm:text-2xl font-bold text-gray-800 dark:text-gray-100">تنظیمات زبان</h1>
                <p className="m-0 mt-1 text-xs text-gray-500 dark:text-gray-400 sm:text-sm">مدیریت زبان‌های فعال سیستم</p>
              </div>
            </div>
            <Space wrap>
              <Button icon={<ReloadOutlined />} onClick={fetchIndex} loading={loading}>
                بروزرسانی
              </Button>
              <Button type="primary" icon={<PlusOutlined />} className={PANEL_PRIMARY_BTN_CLASS} onClick={openCreate}>
                زبان جدید
              </Button>
            </Space>
          </div>

          <div className="rounded-b-xl bg-gray-50 p-4 sm:p-6 dark:bg-gray-900/50">
            <Card className="border border-gray-100 shadow-sm dark:border-gray-700 dark:bg-gray-800 overflow-hidden">
              {loading ? (
                <div className="p-4 sm:p-5">
                  <TableSkeleton rows={8} columns={5} />
                </div>
              ) : (
                <Table rowKey="id" loading={false} columns={columns} dataSource={rows} pagination={false} scroll={{ x: 'max-content' }} />
              )}
            </Card>
          </div>
        </Card>

        <Modal
          wrapClassName="custom-font"
          title={editingId ? 'ویرایش زبان' : 'افزودن زبان'}
          open={formOpen}
          onCancel={() => setFormOpen(false)}
          onOk={onSubmit}
          okText="ذخیره"
          cancelText="لغو"
          confirmLoading={formSubmitting}
          okButtonProps={{ className: PANEL_PRIMARY_BTN_CLASS }}
          destroyOnClose
          width={520}
        >
          <Form form={form} layout="vertical" className="custom-font mt-2" disabled={formLoading}>
            <Spin spinning={formLoading}>
              <Form.Item name="language_name" label="نام زبان" rules={[{ required: true, message: 'نام را وارد کنید' }]}>
                <Input placeholder="مثال: فارسی" />
              </Form.Item>
              <Form.Item name="language_code" label="کد زبان" rules={[{ required: true, message: 'کد را وارد کنید' }]}>
                <Input dir="ltr" placeholder="fa، en، …" />
              </Form.Item>
              <Form.Item name="flag" label="پرچم (کد)" rules={[{ required: true, message: 'پرچم را انتخاب کنید' }]}>
                <Select
                  showSearch
                  optionFilterProp="label"
                  popupClassName="custom-font"
                  placeholder="انتخاب کشور / پرچم"
                  options={flagOptions.map((f) => ({
                    value: f.code,
                    label: `${f.name} (${f.code})`,
                  }))}
                />
              </Form.Item>
              <Form.Item name="status" label="وضعیت" rules={[{ required: true }]}>
                <Select options={STATUS_OPTIONS} popupClassName="custom-font" />
              </Form.Item>
              <Form.Item name="is_rtl" label="راست‌به‌چپ (RTL)" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Spin>
          </Form>
        </Modal>
      </div>
    </MainLayout>
  );
};

export default SuperadminLanguageSettings;
